import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BedDouble } from "lucide-react";

const KIND = {
  zero: "clocked hours but 0 rooms entered",
  fast: "too fast — under the minimum minutes per room",
  slow: "too slow — over the maximum minutes per room",
};

export const HkRoomsSummary = ({ totalRooms, prevSold }) => (
  <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-sm border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="hk-rooms-summary">
    <span className="inline-flex items-center gap-1.5 font-semibold"><BedDouble className="h-3.5 w-3.5" /> Housekeeping check</span>
    <span>Rooms cleaned: <b className="tabular-nums" data-testid="hk-total-rooms">{totalRooms}</b></span>
    <span>Rooms occupied last night: <b className="tabular-nums" data-testid="hk-prev-sold">{prevSold ?? "—"}</b></span>
    {prevSold != null && totalRooms > prevSold && <span className="font-semibold text-red-600 dark:text-red-400">More rooms cleaned than were occupied</span>}
  </div>
);

export const HkRoomsCheckDialog = ({ check, range, onCancel, onConfirm }) => {
  const [ok, setOk] = useState(false);
  useEffect(() => { setOk(false); }, [check]);
  return (
    <Dialog open={!!check} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="hk-check-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><BedDouble className="h-5 w-5 text-amber-500" /> Verify housekeeper rooms</DialogTitle>
          <DialogDescription>
            Check these against the room assignment sheet before saving. The standard is {range[0]}–{range[1]} minutes per room.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(check?.issues || []).map((x) => (
            <div key={x.key} className="rounded-md border border-amber-400/60 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20" data-testid={`hk-issue-${x.eid}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{x.name}</span>
                <span className="font-mono text-xs">{x.hours.toFixed(2)} hrs · {x.rooms} rooms{x.mpr != null ? ` · ${x.mpr.toFixed(1)} min/room` : ""}</span>
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-300">{KIND[x.kind]}</div>
            </div>
          ))}
          {check?.overOccupied && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400" data-testid="hk-over-occupied">
              <b>{check.totalRooms}</b> rooms cleaned, but only <b>{check.prevSold}</b> rooms were occupied last night.
            </div>
          )}
        </div>
        <label className="flex items-start gap-2 text-sm" data-testid="hk-confirm-label">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="hk-confirm-checkbox" />
          I checked these numbers against the housekeeping sheet and they are correct.
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="hk-go-back-btn">Go back &amp; fix</Button>
          <Button onClick={onConfirm} disabled={!ok} data-testid="hk-confirm-btn">Confirmed — save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
